// ⚠️ VISUAL SOURCE OF TRUTH — this is the live guest portal RUNTIME. The portal
// BUILDER preview in the other repo is a hand-maintained twin that must render
// pixel-faithfully like this file (no shared rendering package exists, same
// convention as the website-builder editor↔runtime split):
//   - this runtime:    portal-frontend/src/PortalClient.tsx
//   - builder preview: main-frontend/src/components/builders/portal/PortalPreview.tsx
// When you change the markup/classes here, mirror the change into PortalPreview
// or the admin's design silently drifts from what the guest sees.

import { lazy, Suspense, useLayoutEffect, useState } from "react";
import { useTranslations } from "next-intl";
import { PlatformImprintLink } from "@/components/PlatformImprintLink";
import {
  IconPaperclip,
  IconDownload,
  IconFile,
  IconChevronDown,
  IconChevronRight,
} from "@tabler/icons-react";
import type { Attachment } from "@/types/Attachment";
import type { PortalConfig } from "@/types/PortalConfig";
import type { PortalTicket } from "@/types/Ticket";
import { fetchDownloadUrl } from "@/lib/api";
import {
  buildCardStyle,
  buildPortalStyle,
  decodeTokenExp,
  formatFileSize,
  getCustomText,
  HEADER_WEIGHT_CLASS,
  HERO_IMAGE_AUTO_CLASS,
  HERO_IMAGE_BAND_CLASS,
  isDarkColor,
  resolveHeaderFontWeight,
  resolveHeroImageAspect,
  resolveHeroImagePlacement,
  resolvePortalVars,
  sanitizeExternalUrl,
  sanitizeImageUrl,
  withPortalAlign,
  withPortalBoxAlign,
} from "@/lib/portal-utils";

import { TicketBoundary, TicketSectionFallback } from "@/components/ticket/TicketBoundary";
import { DocumentRowsFallback, DocumentsBoundary } from "@/components/documents/DocumentsBoundary";

// Lazy: the ticket section (QR encoder, wallet badges) only enters the page
// when the event enabled tickets — portals without one never request the chunk.
// A chunk that fails to load (404 after a deploy) is swallowed by TicketBoundary
// so the attachments below always render (D7).
const TicketSection = lazy(() => import("@/components/ticket/TicketSection"));
// Lazy for the same reason: the document-template rows (render on tap, wallet
// / calendar / PDF links) only load when an open card lists a template. A
// failed chunk is swallowed by DocumentsBoundary — the card and its uploaded
// files stay.
const DocumentRows = lazy(() => import("@/components/documents/DocumentRows"));

/**
 * An OPAQUE hex page colour (`#rgb` / `#rrggbb`). Deliberately its own
 * constant: PortalPage's HEX_COLOR also admits `#rgba` / `#rrggbbaa` (and
 * importing it would be a cycle), and portal-utils is the cockpit twin.
 */
const OPAQUE_HEX = /^#(?:[0-9a-f]{3}|[0-9a-f]{6})$/i;

type Props = {
  token: string;
  guestName: string;
  eventName: string;
  attachments: Attachment[];
  portalConfig?: PortalConfig | undefined;
  /** Flat `{{variable}}` context from the backend (see PortalResponse). */
  variables?: Record<string, unknown> | undefined;
  /** Ticket block from the portal response; null/undefined = no section. */
  ticket?: PortalTicket | null | undefined;
  locale: string;
};

/**
 * The operator's text for THIS locale, then its base language — and nothing
 * else. A value that is not a string (a hand-PUT `null`, a stray number) is
 * absent. Twin of the same helper in the cockpit's `PortalPreview.tsx`.
 */
function pickOwnLocaleText(map: Record<string, string> | undefined, locale: string): string {
  for (const key of [locale, locale.split("-")[0]!]) {
    const value = map?.[key];
    if (typeof value === "string") return value;
  }
  return "";
}

export function PortalClient({
  token,
  guestName,
  eventName,
  attachments,
  portalConfig,
  variables,
  ticket,
  locale,
}: Props) {
  const t = useTranslations();

  // The page colour reaches the WHOLE canvas, not just the wrapper: `body`
  // paints `var(--portal-bg)` (styles/index.css), and that is what shows under
  // a short page, in the safe areas and when iOS over-scrolls — #f5f5f5, a
  // light band under a dark portal. A layout effect, so it lands in the same
  // frame in which PortalPage's dismissBootScreen clears the boot script's
  // colour (no flash of the default). Only an opaque colour: a translucent
  // one (the opacity slider stores #rgba / #rrggbbaa) keeps today's default
  // underneath, exactly as the builder shows it; junk sets nothing. Removed on
  // unmount, so the error screen gets the default back.
  const pageBg = portalConfig?.backgroundColor;
  useLayoutEffect(() => {
    if (typeof pageBg !== "string" || !OPAQUE_HEX.test(pageBg)) return;
    const root = document.documentElement;
    root.style.setProperty("--portal-bg", pageBg);
    return () => {
      root.style.removeProperty("--portal-bg");
    };
  }, [pageBg]);

  const [expandedIds, setExpandedIds] = useState<Set<string>>(() => {
    const expandByDefault = portalConfig?.expandAttachmentsByDefault ?? true;
    return expandByDefault ? new Set(attachments.map((a) => a.attachmentId)) : new Set();
  });

  const toggleExpand = (id: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  // H31: a failed download-URL exchange must be VISIBLE — swallowing the
  // rejection left the guest with a silent dead click. Surface an inline
  // alert (the portal has no toast system) and clear it on the next success.
  const [downloadError, setDownloadError] = useState<string | null>(null);
  // Per URL, like the ticket banner: a replacement picture gets its own
  // chance without the page having to be remounted.
  const [brokenHero, setBrokenHero] = useState<string | null>(null);
  const handleDownload = async (fileId: string) => {
    try {
      const result = await fetchDownloadUrl(token, fileId);
      setDownloadError(null);
      window.open(result.url, "_blank", "noopener,noreferrer");
    } catch {
      setDownloadError(t("errors.downloadFailed"));
    }
  };

  const def = portalConfig?.defaultLocale;
  // Resolve `{{variable}}` placeholders against the backend-supplied context
  // for every guest-facing text field. `guestName`/`eventName` are always
  // present (legacy single-`{guestName}` still honoured inside resolvePortalVars
  // for portals authored before the double-brace unification).
  const ctx: Record<string, unknown> = { guestName, eventName, ...(variables ?? {}) };
  const subtitle = resolvePortalVars(
    getCustomText(portalConfig?.subtitleText, locale, t("documentsFor", { guestName }), def),
    ctx,
  );

  const eyebrow = resolvePortalVars(getCustomText(portalConfig?.eyebrowText, locale, "", def), ctx);
  const titleText = resolvePortalVars(
    getCustomText(portalConfig?.pageTitleText, locale, eventName, def),
    ctx,
  );
  const emptyStateText = resolvePortalVars(
    getCustomText(portalConfig?.emptyStateText, locale, t("noDocumentsAvailable"), def),
    ctx,
  );
  // The worker already applies the ticket gate (disabled / nothing to show /
  // ineligible ⇒ `ticket: null`), so a present block means something renders —
  // unless the lazy chunk failed (D7): then the ticket is gone and the page
  // must not stay bare, so the empty card comes back.
  const [ticketFailed, setTicketFailed] = useState(false);
  const ticketOn = ticket?.enabled === true && !ticketFailed;
  const footerText = resolvePortalVars(
    getCustomText(portalConfig?.footerText, locale, t("poweredBy"), def),
    ctx,
  );
  // The privacy link used to read the English word "Privacy" in all 57
  // locales with no way for the operator to change it.
  //
  // DELIBERATE deviation from the other portal texts: this label does NOT
  // borrow another language. Every other field falls back to a generic
  // bundled string ("Documents for X"), so an operator's text in the wrong
  // language still beats it. This one falls back to a real translation in all
  // 57 locales (`privacyLink`) — so borrowing would put "Privacy" in front of
  // a German guest again, which is literally the complaint this wave started
  // with. Own locale → own base language → the bundled translation.
  //
  // `getCustomText` already turns a CLEARED entry into the bundled default.
  // The `|| t(…)` catches the one case it cannot see: a label whose
  // `{{variables}}` resolve to nothing AFTER it. Without it that rendered
  // `<a href="…"></a>` — a nameless, zero-width but still focusable link on a
  // legally required target.
  //
  // `typeof`, like `getCustomText`: the map is read raw here, so a hand-PUT
  // `null` or a stray number must count as absent, not crash the portal.
  const ownPrivacyLabel = pickOwnLocaleText(portalConfig?.privacyLabelText, locale);
  const privacyLabel =
    resolvePortalVars(ownPrivacyLabel, ctx).trim() || t("privacyLink");
  // The imprint label follows the privacy label exactly — same own-locale-only
  // chain, same reason (`imprintLink` is a real translation in all 57 locales,
  // so borrowing would put a foreign word on a legally required link).
  const ownImprintLabel = pickOwnLocaleText(portalConfig?.imprintLabelText, locale);
  const imprintLabel =
    resolvePortalVars(ownImprintLabel, ctx).trim() || t("imprintLink");

  const cardStyle = buildCardStyle(portalConfig);
  // Same signal as the ticket card (TicketSection): the wallet badges of the
  // document rows take the Apple outline / light ring on a dark card.
  const darkCard = isDarkColor(portalConfig?.cardBackgroundColor);
  const safeLogo = sanitizeImageUrl(portalConfig?.logoUrl);
  const safeIcon = sanitizeImageUrl(portalConfig?.heroIconUrl);
  // L12 second wall: only http(s) privacy links ever reach the href.
  const safePrivacyUrl = sanitizeExternalUrl(portalConfig?.privacyPolicyUrl);
  // Same wall for the imprint, and deliberately the same helper: `http://` is
  // allowed here (an imprint on a plain host is still the legal page), only
  // `javascript:` / `data:` / junk are refused.
  const safeImprintUrl = sanitizeExternalUrl(portalConfig?.imprintUrl);

  // Tailwind cannot see a class it did not read in the source, so the title's
  // weight resolves through the literal lookup map in portal-utils.
  const headerWeight = resolveHeaderFontWeight(portalConfig?.headerFontWeight);
  // Per-block alignment. An unset block adds NO class (withPortalAlign returns
  // the base string unchanged), so an untouched portal's markup is byte-for-
  // byte what it was before the setting existed.
  const align = portalConfig?.textAlign;

  // Header picture. Two rules, both paid for by the ticket banner:
  //   - the band is reserved ONLY at a KNOWN aspect. Without one the picture
  //     is drawn at `h-auto` and nothing is guessed — a guessed ratio cropped
  //     18-82 % off real operators' pictures.
  //   - `onError` collapses it. A band held open for bytes that never arrive
  //     (a deleted object, an R2 blip) is a permanent void at the top of the
  //     page, which is worse than the shift the reservation prevents.
  const safeHero = sanitizeImageUrl(portalConfig?.heroImageUrl);
  const heroImage = safeHero && brokenHero !== safeHero ? safeHero : null;
  const heroAspect = resolveHeroImageAspect(portalConfig?.heroImageAspect);
  const heroPlacement = resolveHeroImagePlacement(portalConfig?.heroImagePlacement);
  const heroImageEl = heroImage ? (
    <img
      src={heroImage}
      alt=""
      data-testid="portal-hero-image"
      className={heroAspect === null ? HERO_IMAGE_AUTO_CLASS : HERO_IMAGE_BAND_CLASS}
      // An inline ratio, not `aspect-[w/h]`: an arbitrary Tailwind value built
      // at runtime is erased from the stylesheet exactly like `text-${align}`.
      style={heroAspect === null ? undefined : { aspectRatio: `${heroAspect}` }}
      onError={() => setBrokenHero(heroImage)}
    />
  ) : null;

  const secondaryStyle: React.CSSProperties = portalConfig?.secondaryTextColor
    ? { color: portalConfig.secondaryTextColor }
    : { color: "var(--portal-fg-muted)" };
  // Download icon: use the admin's accent colour only if set, otherwise the
  // muted icon colour (matches the paperclip/file icons) — never a hardcoded
  // blue.
  const accentStyle: React.CSSProperties = portalConfig?.accentColor
    ? { color: portalConfig.accentColor }
    : secondaryStyle;

  const showExpiry = !portalConfig?.hideExpiryBanner;
  const showFileSizes = !portalConfig?.hideFileSizes;

  return (
    // `min-h-dvh`: #root is no flex container, so `flex-1` never stretched
    // this wrapper — a page shorter than the window ended in `body`'s colour.
    // Runtime only: the builder preview must not grow to the window (twin).
    <div
      className="flex-1 flex flex-col items-center px-4 py-8 sm:py-12 min-h-dvh"
      style={buildPortalStyle(portalConfig)}
    >
      <div className="w-full max-w-150">
        {/* Logo + company */}
        {(safeLogo || portalConfig?.companyName) && (
          <div className="mb-4">
            {safeLogo && (
              <img
                src={safeLogo}
                alt={portalConfig?.companyName ?? eventName}
                className="h-10 object-contain mb-2"
              />
            )}
            {portalConfig?.companyName && (
              <p className="text-sm" style={secondaryStyle}>
                {portalConfig.companyName}
              </p>
            )}
          </div>
        )}

        {/* Header picture, above the text block */}
        {heroPlacement === "above" && heroImageEl}

        {/* Eyebrow + hero icon + title */}
        <div className="mb-8">
          {eyebrow && (
            <p
              className={withPortalAlign("text-xs uppercase tracking-wide mb-2", align, "eyebrow")}
              style={secondaryStyle}
              data-testid="portal-eyebrow"
            >
              {eyebrow}
            </p>
          )}
          {safeIcon && (
            <img
              src={safeIcon}
              alt=""
              // The icon sits directly on top of the H1 and is a BOX, not a
              // line of text: `text-*` cannot move a block-level image with a
              // width of its own, so it follows the title's alignment through
              // its margins.
              className={withPortalBoxAlign("w-12 h-12 mb-3 object-contain", align, "pageTitle")}
              data-testid="portal-hero-icon"
            />
          )}
          <h1
            className={withPortalAlign(
              `text-3xl ${HEADER_WEIGHT_CLASS[headerWeight]}`,
              align,
              "pageTitle",
            )}
          >
            {titleText}
          </h1>
          <p className={withPortalAlign("text-base mt-1", align, "subtitle")} style={secondaryStyle}>
            {subtitle}
          </p>

          {showExpiry &&
            (() => {
              const expiry = decodeTokenExp(token);
              if (!expiry) return null;
              const formatted = expiry.toLocaleDateString(locale, {
                day: "numeric",
                month: "long",
                year: "numeric",
              });
              return (
                <p
                  className="text-xs mt-2"
                  style={{ color: "var(--portal-fg-muted)" }}
                  data-testid="portal-expiry-banner"
                >
                  {t("expiresOn", { date: formatted })}
                </p>
              );
            })()}
        </div>

        {/* …or below it */}
        {heroPlacement === "below" && heroImageEl}

        {/* Download failure surface (H31) */}
        {downloadError && (
          <div
            role="alert"
            data-testid="portal-download-error"
            className="border px-4 py-3 mb-4 text-sm"
            style={{ ...cardStyle, borderColor: "#dc2626", color: "#dc2626" }}
          >
            {downloadError}
          </div>
        )}

        {/* Ticket: QR + wallet passes + PDF (own card first, before the documents) */}
        {ticketOn && ticket && (
          <TicketBoundary onFailed={() => setTicketFailed(true)}>
            <Suspense fallback={<TicketSectionFallback cardStyle={cardStyle} />}>
              <TicketSection
                token={token}
                ticket={ticket}
                guestName={guestName}
                eventName={eventName}
                portalConfig={portalConfig}
                variables={variables}
                locale={locale}
              />
            </Suspense>
          </TicketBoundary>
        )}

        {/* Attachments. The empty card only when the guest truly has nothing:
            the ticket (QR / wallet / PDF) is a document too, so a ticket-only
            portal renders the ticket card and no "no documents" claim below it. */}
        {attachments.length === 0 ? (
          !ticketOn && (
            <div className="border p-8 text-center" style={cardStyle} data-testid="portal-empty-state">
              <IconPaperclip
                size={32}
                className={withPortalBoxAlign("mb-3", align, "emptyState", "mx-auto")}
                style={secondaryStyle}
              />
              <p className={withPortalAlign("text-sm", align, "emptyState")} style={secondaryStyle}>
                {emptyStateText}
              </p>
            </div>
          )
        ) : (
          <div className="flex flex-col gap-3">
            {attachments.map((att) => {
              const isExpanded = expandedIds.has(att.attachmentId);
              const files = att.files ?? [];
              // Templates rendered on tap (absent on an older worker).
              const documents = att.documents ?? [];
              const borderStyle = portalConfig?.cardBorderColor
                ? { borderColor: portalConfig.cardBorderColor }
                : undefined;
              const noFiles = (
                <p className="text-xs" style={secondaryStyle}>
                  {t("noFilesAvailable")}
                </p>
              );
              return (
                <div key={att.attachmentId} className="border overflow-hidden" style={cardStyle}>
                  <button
                    className="flex items-center gap-3 w-full px-5 py-4 hover:opacity-80 transition-opacity"
                    onClick={() => toggleExpand(att.attachmentId)}
                  >
                    {isExpanded ? (
                      <IconChevronDown size={18} className="shrink-0" style={secondaryStyle} />
                    ) : (
                      <IconChevronRight size={18} className="shrink-0" style={secondaryStyle} />
                    )}
                    <IconPaperclip size={18} className="shrink-0" style={secondaryStyle} />
                    <span className="font-medium truncate flex-1 text-left">{att.name}</span>
                    <span className="text-xs shrink-0" style={secondaryStyle}>
                      {t("fileCount", { count: files.length + documents.length })}
                    </span>
                  </button>

                  {isExpanded && (files.length > 0 || documents.length > 0) && (
                    <div className="border-t" style={borderStyle}>
                      {files.map((file) => (
                        <div
                          key={file.attachmentFileId}
                          className="flex items-center gap-3 px-5 py-3 hover:opacity-80 transition-opacity"
                        >
                          <IconFile size={16} className="shrink-0" style={secondaryStyle} />
                          <span className="text-sm truncate flex-1">{file.fileName}</span>
                          {showFileSizes && (
                            <span className="text-xs shrink-0" style={secondaryStyle}>
                              {formatFileSize(file.fileSize)}
                            </span>
                          )}
                          <button
                            className="p-1.5 rounded-lg hover:opacity-80 transition-opacity"
                            style={accentStyle}
                            onClick={() => handleDownload(file.attachmentFileId)}
                            aria-label={`download ${file.fileName}`}
                          >
                            <IconDownload size={16} />
                          </button>
                        </div>
                      ))}
                      {documents.length > 0 && (
                        <DocumentsBoundary
                          fallback={
                            // Never "No files": the header still counts the
                            // templates, they are just not reachable now.
                            // Polite like the rows' own error lines.
                            <div className="px-5 py-3">
                              <p aria-live="polite" className="text-xs" style={secondaryStyle}>
                                {t("documents.unavailable")}
                              </p>
                            </div>
                          }
                        >
                          <Suspense
                            fallback={<DocumentRowsFallback documents={documents} secondaryStyle={secondaryStyle} />}
                          >
                            <DocumentRows
                              token={token}
                              attachmentId={att.attachmentId}
                              documents={documents}
                              locale={locale}
                              darkCard={darkCard}
                              secondaryStyle={secondaryStyle}
                              accentStyle={accentStyle}
                            />
                          </Suspense>
                        </DocumentsBoundary>
                      )}
                    </div>
                  )}

                  {/* "No files" only when the card has neither an upload nor a template. */}
                  {isExpanded && files.length === 0 && documents.length === 0 && (
                    <div className="border-t px-5 py-3" style={borderStyle}>
                      {noFiles}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Contact + footer. The imprint MUST be part of this condition: a
            portal whose only footer content is a legal notice would otherwise
            render no footer at all. Sanitised values, not the raw config, or
            an unusable URL mounts an empty bar. */}
        {(portalConfig?.contactEmail || safePrivacyUrl || safeImprintUrl) && (
          <div className="mt-6 text-center text-xs space-x-3" style={secondaryStyle}>
            {portalConfig?.contactEmail && (
              <a href={`mailto:${portalConfig.contactEmail}`} className="hover:underline">
                {portalConfig.contactEmail}
              </a>
            )}
            {safePrivacyUrl && (
              <a
                href={safePrivacyUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="hover:underline"
                data-testid="portal-privacy-link"
              >
                {privacyLabel}
              </a>
            )}
            {safeImprintUrl && (
              <a
                href={safeImprintUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="hover:underline"
                data-testid="portal-imprint-link"
              >
                {imprintLabel}
              </a>
            )}
          </div>
        )}

        {portalConfig?.showPoweredBy !== false && (
          <div className="mt-8 text-center">
            <p className={withPortalAlign("text-xs", align, "footer")} style={secondaryStyle}>
              {footerText}
            </p>
          </div>
        )}

        {/* Outside both conditions above, on purpose — see the component. */}
        <PlatformImprintLink style={secondaryStyle} />
      </div>
    </div>
  );
}
